import React, { useMemo } from 'react';

type AppBankAccountPickerAccount = {
  id?: string;
  bankName?: string;
  bankDisplayName?: string;
  bankLast4?: string;
  last4?: string;
  logo?: string | null;
  bankLogoUrl?: string | null;
  cardImageUrl?: string | null;
  [key: string]: any;
};

type Props<T extends AppBankAccountPickerAccount> = {
  accounts: T[];
  selectedAccountId?: string | null;
  selectedAccount?: T | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onMissingPress: () => void;
  onAddAccountPress: () => void;
  onSelectAccount: (account: T) => void;
  getAccountName: (account: T) => string;
  getAccountLast4: (account: T) => string;
  getAccountLogo: (account: T) => string | null;
  getAccountWarning?: (account: T) => string | null;
  selectedTitle?: string;
  selectedSubtitle?: string;
  emptyTitle?: string;
  emptySubtitle?: string;
  emptyCtaLabel?: string;
  addAccountLabel?: string;
  addAccountLoadingLabel?: string;
  disabled?: boolean;
  addDisabled?: boolean;
  savingAccountId?: string | null;
  loadingAdd?: boolean;
  hideConnectCta?: boolean;
  getAccountType?: (account: T) => string;
  onRelinkPress?: (account: T) => void;
  doesAccountRequireRelink?: (account: T) => boolean;
};

const formatAccountTitle = (name: string, last4: string, maxNameChars = 16) => {
  const trimmedName = String(name || '').trim();
  const trimmedLast4 = String(last4 || '').trim();

  if (!trimmedName) return trimmedLast4 ? `Bank ${trimmedLast4}` : 'Connected bank';
  if (!trimmedLast4) return trimmedName;

  const compactName = trimmedName.length > maxNameChars
    ? `${trimmedName.slice(0, maxNameChars).trimEnd()}...`
    : trimmedName;

  return `${compactName} ${trimmedLast4}`;
};

const buildAccountLast4Label = (last4: string) => {
  const trimmedLast4 = String(last4 || '').trim();
  return trimmedLast4 ? `•••• ${trimmedLast4}` : '';
};

const splitAccountTitleParts = (title: string, fallbackName: string, fallbackLast4: string) => {
  const trimmedTitle = String(title || '').trim();
  const trimmedFallbackName = String(fallbackName || '').trim();
  const trimmedFallbackLast4 = String(fallbackLast4 || '').trim();
  const fallbackLast4Label = buildAccountLast4Label(trimmedFallbackLast4);

  if (!trimmedTitle) {
    return {
      primary: trimmedFallbackName || (trimmedFallbackLast4 ? 'Bank' : 'Connected bank'),
      last4Label: fallbackLast4Label,
    };
  }

  if (!trimmedFallbackLast4) {
    return {
      primary: trimmedTitle,
      last4Label: '',
    };
  }

  const trailingLast4Match = trimmedTitle.match(/(?:\u2022{4}\s*)?(\d{4})$/);
  if (trailingLast4Match) {
    const matchedLast4 = trailingLast4Match[1];
    const primary = trimmedTitle.slice(0, trailingLast4Match.index).trim().replace(/[•\-\u2013\u2014]+$/g, '').trim();
    return {
      primary: primary || trimmedFallbackName || 'Bank',
      last4Label: buildAccountLast4Label(matchedLast4),
    };
  }

  return {
    primary: trimmedFallbackName || trimmedTitle,
    last4Label: fallbackLast4Label,
  };
};

const ChevronDownIcon = ({ open }: { open: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);

const BuildingIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01" />
    <path d="M16 6h.01" />
    <path d="M12 6h.01" />
    <path d="M12 10h.01" />
    <path d="M12 14h.01" />
    <path d="M16 10h.01" />
    <path d="M16 14h.01" />
    <path d="M8 10h.01" />
    <path d="M8 14h.01" />
  </svg>
);

export default function AppBankAccountPickerWeb<T extends AppBankAccountPickerAccount>({
  accounts,
  selectedAccountId,
  selectedAccount,
  isOpen,
  onOpenChange,
  onMissingPress,
  onAddAccountPress,
  onSelectAccount,
  getAccountName,
  getAccountLast4,
  getAccountLogo,
  getAccountWarning,
  selectedTitle,
  selectedSubtitle = 'Selected account',
  emptyTitle = 'No bank connected',
  emptySubtitle = 'Required before you can receive payments',
  emptyCtaLabel = 'Connect bank now',
  addAccountLabel = 'Add bank account',
  addAccountLoadingLabel = 'Opening bank connection...',
  disabled = false,
  addDisabled = false,
  savingAccountId,
  loadingAdd = false,
  hideConnectCta = false,
  getAccountType,
  onRelinkPress,
  doesAccountRequireRelink,
}: Props<T>) {
  const resolvedSelectedAccount = useMemo(
    () => selectedAccount || accounts.find((account) => account.id === selectedAccountId) || null,
    [accounts, selectedAccount, selectedAccountId]
  );

  const hasConnectedAccount = Boolean(resolvedSelectedAccount);
  const selectedLogo = resolvedSelectedAccount ? getAccountLogo(resolvedSelectedAccount) : null;
  const selectedName = resolvedSelectedAccount ? getAccountName(resolvedSelectedAccount) : '';
  const selectedLast4 = resolvedSelectedAccount ? getAccountLast4(resolvedSelectedAccount) : '';
  const selectedType = resolvedSelectedAccount && getAccountType ? getAccountType(resolvedSelectedAccount) : '';
  const selectedNameAndType = selectedType ? `${selectedName} ${selectedType}` : selectedName;
  const fallbackSelectedTitle = formatAccountTitle(selectedNameAndType, selectedLast4, 14);
  const selectedTitleParts = splitAccountTitleParts(selectedTitle || fallbackSelectedTitle, selectedNameAndType, selectedLast4);

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          if (!hasConnectedAccount) {
            onMissingPress();
            return;
          }
          onOpenChange(!isOpen);
        }}
        disabled={disabled}
        className={`relative w-full bg-slate-50 transition-all ${
          hasConnectedAccount
            ? 'min-h-[58px] rounded-[1.4rem] px-3.5 py-2.5'
            : hideConnectCta
              ? 'rounded-[1.4rem] px-3.5 py-3'
              : 'rounded-[1.4rem] px-3.5 py-3'
        } ${disabled ? 'opacity-65' : ''}`}
      >
        {!hasConnectedAccount ? (
          <div className="absolute left-0 top-1 bottom-1 w-1.5 rounded-l-[16px] bg-amber-400" />
        ) : null}

        <div className="flex items-center justify-between gap-2.5">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            {hasConnectedAccount && selectedLogo ? (
              <div className="flex h-7 w-7 items-center justify-center">
                <div className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-white">
                  <img src={selectedLogo} alt={selectedName || 'Bank'} className="h-4.5 w-4.5 object-contain" />
                </div>
              </div>
            ) : hasConnectedAccount ? (
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-[12px] font-black text-slate-600">
                {String(selectedName || 'B').trim().charAt(0).toUpperCase() || 'B'}
              </div>
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-amber-100 bg-amber-50 text-amber-700">
                <BuildingIcon />
              </div>
            )}

            <div className="min-w-0 flex-1 text-left">
              {hasConnectedAccount ? (
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-[12px] font-bold text-slate-900">{selectedTitleParts.primary}</span>
                  {selectedTitleParts.last4Label ? (
                    <span className="shrink-0 text-[12px] font-bold text-slate-900">{selectedTitleParts.last4Label}</span>
                  ) : null}
                </div>
              ) : (
                <p className="text-[13px] font-bold text-slate-900">{emptyTitle}</p>
              )}

              <p
                className={`mt-0.5 text-[9px] font-semibold ${
                  hasConnectedAccount
                    ? selectedSubtitle === 'Relink required'
                      ? 'text-[#C25E00]'
                      : 'text-slate-400'
                    : 'text-amber-700'
                }`}
              >
                {hasConnectedAccount ? selectedSubtitle : emptySubtitle}
              </p>
            </div>
          </div>

          {hasConnectedAccount ? (
            <div className="flex shrink-0 items-center gap-2">
              {resolvedSelectedAccount && doesAccountRequireRelink?.(resolvedSelectedAccount) && onRelinkPress ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRelinkPress(resolvedSelectedAccount);
                  }}
                  className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[9px] font-black uppercase tracking-wide text-[#C25E00]"
                >
                  Relink now
                </button>
              ) : null}
              <ChevronDownIcon open={isOpen} />
            </div>
          ) : hideConnectCta ? null : (
            <div className="flex shrink-0 items-center gap-2 rounded-[12px] bg-[#0D9488] px-3 py-2 text-white">
              <span className="text-[12px] font-bold">{emptyCtaLabel}</span>
              <ChevronRightIcon />
            </div>
          )}
        </div>
      </button>

      {hasConnectedAccount ? (
        <div
          className="overflow-hidden transition-all duration-200 ease-out"
          style={{
            maxHeight: isOpen ? 320 : 0,
            opacity: isOpen ? 1 : 0,
          }}
        >
          <div className="mt-2 overflow-hidden rounded-[1.4rem] bg-slate-50 border border-slate-100/80">
            {accounts.map((account) => {
              const accountId = String(account.id || '');
              const name = getAccountName(account);
              const last4 = getAccountLast4(account);
              const type = getAccountType ? getAccountType(account) : '';
              const nameAndType = type ? `${name} ${type}` : name;
              const logo = getAccountLogo(account);
              const isSelected = accountId === selectedAccountId;
              const warning = getAccountWarning?.(account) || null;
              const isSaving = savingAccountId === accountId;
              const accountTitle = formatAccountTitle(nameAndType, last4, 22);
              const accountTitleParts = splitAccountTitleParts(accountTitle, nameAndType, last4);

              return (
                <button
                  key={`bank-account-${accountId}`}
                  type="button"
                  onClick={() => onSelectAccount(account)}
                  disabled={disabled || Boolean(savingAccountId)}
                  className={`flex min-h-[58px] w-full items-center justify-between gap-2.5 border-b border-slate-200 px-3.5 py-2.5 text-left last:border-b-0 ${
                    isSelected ? 'bg-[#edf9f6]' : 'bg-white hover:bg-slate-50'
                  }`}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2.5">
                    {logo ? (
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white">
                        <img src={logo} alt={name || 'Bank'} className="h-4.5 w-4.5 object-contain" />
                      </div>
                    ) : (
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-[12px] font-black text-slate-600">
                        {name.charAt(0).toUpperCase() || 'B'}
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className={`truncate text-[12px] font-bold ${isSelected ? 'text-[#0c7f76]' : 'text-slate-900'}`}>{accountTitleParts.primary}</span>
                        {accountTitleParts.last4Label ? (
                          <span className={`shrink-0 text-[12px] font-bold ${isSelected ? 'text-[#0c7f76]' : 'text-slate-900'}`}>{accountTitleParts.last4Label}</span>
                        ) : null}
                      </div>
                      {warning ? (
                        <p className="mt-0.5 text-[9px] font-bold text-[#C25E00]">{warning}</p>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {doesAccountRequireRelink?.(account) && onRelinkPress ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onRelinkPress(account);
                        }}
                        className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-[#C25E00]"
                      >
                        Relink now
                      </button>
                    ) : null}

                    {isSaving ? (
                      <div className="h-4 w-4 rounded-full border-2 border-teal-200 border-t-teal-600 animate-spin" />
                    ) : isSelected ? (
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0d9488] text-white">
                        <CheckIcon />
                      </div>
                    ) : null}
                  </div>
                </button>
              );
            })}

            <button
              type="button"
              onClick={onAddAccountPress}
              disabled={disabled || addDisabled}
              className="flex min-h-[54px] w-full items-center justify-between gap-2.5 bg-white px-3.5 py-2.5 text-left hover:bg-slate-50 disabled:opacity-60"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2.5">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                  <PlusIcon />
                </div>
                <span className="truncate text-[12px] font-bold text-slate-900">
                  {loadingAdd ? addAccountLoadingLabel : addAccountLabel}
                </span>
              </div>
              <span className="text-slate-400">
                <ChevronRightIcon />
              </span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
